<!--
  Author: Gary Hammock
  Version: 0.1.0
  Description: A renderer for publications on Vue pages.
  Requires: date-fns
  See: https://www.npmjs.com/package/date-fns
-->

<template>
  <p class="publication-container">
    <span class="publication-author">
      {{ authorString }},
    </span>
    <span
      v-if="year"
      class="publication-year"
    >
      {{ year.toString(10) }}.
    </span>
    <span class="publication-title">
      {{ title }},
    </span>
    <span
      v-if="conference"
      class="publication-conference"
    >
      {{ conference }},
    </span>
    <span
      v-if="publisher"
      class="publication-publisher"
    >
      {{ publisher }},
    </span>
    <span
      v-if="paperNumber"
      class="publication-paper-number"
    >
      ({{ paperNumber }}).
    </span>
    <span
      v-if="month"
      class="publication-month"
    >
      {{ month }},
    </span>
    <span
      v-if="volume"
      class="publication-volume"
    >
      {{ volume }},
    </span>
    <span
      v-if="pages"
      class="publication-pages"
    >
      {{ pageCardinality }} {{ pages }}.
    </span>
    <span
      v-if="publicationLocation"
      class="publication-location"
    >
      {{ publicationLocation }}.
    </span>
    <span
      v-if="publicationDate"
      class="publication-date"
    >
      {{ dateString }}
    </span>
    <br v-if="doi">
    <span
      v-if="doi"
      class="publication-doi"
    >
      DOI:
      <a
        :href="`https://doi.org/${doi}`"
        rel="nofollow"
        target="_blank"
      >
        {{ doi }}
      </a>
    </span>
    <br v-if="url">
    <span
      v-if="url"
      class="publication-url"
    >
      Online:
      <a
        :href="url"
        rel="nofollow"
        target="_blank"
      >
        {{ url }}
      </a>
    </span>
  </p>
</template>

<script>
import { format } from 'date-fns'

export default {
  name: 'PublicationRenderer',

  props: {
    /**
     * The author(s) of the publication.
     */
    authors: {
      type: [String, Array],
      required: true
    },

    /**
     * The title of the publication.
     */
    title: {
      type: String,
      required: true
    },

    /**
     * (Optional) The full, four-digit year of the publication.
     */
    year: {
      type: Number,
      required: false,
      default: null
    },

    /**
     * (Optional) The month of publication, preferably as a name (e.g. "September").
     */
    month: {
      type: String,
      required: false,
      default: ''
    },

    /**
     * (Optional) The volume of the publication.
     */
    volume: {
      type: String,
      required: false,
      default: ''
    },

    /**
     * (Optional) A string representing the relevant pages of a publication.  This can be either discreet values
     * or a range of values (e.g. "391", "3, 8-9", and "14-17").  Any entry with a "-" (dash) or a "," (comma)
     * causes the page cardinality to render as "pp."; otherwise "p.".
     */
    pages: {
      type: String,
      required: false,
      default: ''
    },

    /**
     * (Optional) The name of the conference/conference proceedings that published the publication.
     */
    conference: {
      type: String,
      required: false,
      default: ''
    },

    /**
     * (Optional) The name of the publisher.
     */
    publisher: {
      type: String,
      required: false,
      default: ''
    },

    /**
     * (Optional) The identification number of the paper, ISBN, etc.
     */
    paperNumber: {
      type: String,
      required: false,
      default: ''
    },

    /**
     * (Optional) The location of the conference venue, publisher location.
     */
    publicationLocation: {
      type: String,
      required: false,
      default: ''
    },

    /**
     * (Optional) The date of publication.
     */
    publicationDate: {
      type: String,
      required: false,
      default: ''
    },

    /**
     * (Optional) The Digital Object Identifier (DOI) of the publication.
     */
    doi: {
      type: String,
      required: false,
      default: ''
    },

    /**
     * (Optional) A URL to the source.
     */
    url: {
      type: String,
      required: false,
      default: ''
    }
  },

  computed: {
    /**
     * Formats the author string based on whether a single author is provided or an array.
     *
     * @returns {string | Array.<string>}
     */
    authorString () {
      if (Array.isArray(this.authors)) {
        return this.authors.join(', ')
      } else {
        return this.authors
      }
    },

    /**
     * Formats the publication date object to an ISO-8601 string.
     *
     * @returns {string}
     */
    dateString () {
      const dateObj = new Date(this.publicationDate)

      if (!this.publicationDate || isNaN(dateObj.getTime())) {
        return ''
      } else {
        return format(dateObj, 'yyyy-MM-dd')
      }
    },

    /**
     * Returns either "pp." or "p." depending on the format of the pages property.
     *
     * @returns {string}
     */
    pageCardinality () {
      if (this.pages && ((this.pages.indexOf('-') !== -1) || (this.pages.indexOf(',') !== -1))) {
        return 'pp.'
      } else {
        return 'p.'
      }
    }
  }
}
</script>

<style scoped>
.publication-title {
  font-style: italic;
}

.publication-conference, .publication-paper-number, .publication-location,
.publication-date, .publication-doi, .publication-url, .publication-publisher,
.publication-volume, .publication-month, .publication-pages {
  font-size: 90%;
}

.publication-publisher {
  font-weight: bold;
}
</style>
